import {
    type EditorState,
    type Extension,
    RangeSetBuilder,
    StateEffect,
    StateField,
} from "@codemirror/state";
import {
    Decoration,
    type DecorationSet,
    EditorView,
    ViewPlugin,
    type ViewUpdate,
} from "@codemirror/view";
import type {
    IntelligenceSemanticToken,
    SemanticTokenType,
} from "@magic-md/engine-client/protocol";
import { createIntelligenceSessionResolver } from "./session";
import type { IntelligenceConfig } from "./types";

const tokenClassMap: Record<SemanticTokenType, string> = {
    directive: "cm-intelligence-token-directive",
    definitionId: "cm-intelligence-token-definition-id",
    pluginName: "cm-intelligence-token-plugin-name",
    refPath: "cm-intelligence-token-ref-path",
    configKey: "cm-intelligence-token-config-key",
    fenceLang: "cm-intelligence-token-fence-lang",
    forKeyword: "cm-intelligence-token-for-keyword",
    metaKeyword: "cm-intelligence-token-meta-keyword",
    importKeyword: "cm-intelligence-token-import-keyword",
    useKeyword: "cm-intelligence-token-use-keyword",
    contentKeyword: "cm-intelligence-token-content-keyword",
    namespace: "cm-intelligence-token-namespace",
    filePath: "cm-intelligence-token-file-path",
    symbolName: "cm-intelligence-token-symbol-name",
};

export function buildSemanticTokenDecorations(
    state: EditorState,
    tokens: IntelligenceSemanticToken[],
): DecorationSet {
    if (tokens.length === 0) return Decoration.none;

    // Resolve to absolute positions and sort by `from`. The server may
    // return tokens in arbitrary order, but RangeSetBuilder requires
    // strictly non-decreasing `from` and throws "Ranges must be added
    // sorted by `from` position and `startSide`" otherwise — which would
    // crash the StateField update and freeze the editor.
    const resolved: { from: number; to: number; type: SemanticTokenType }[] = [];
    for (const token of tokens) {
        if (token.line < 1 || token.line > state.doc.lines) continue;
        const line = state.doc.line(token.line);
        const from = Math.min(line.from + Math.max(0, token.column), line.to);
        const to = Math.min(from + Math.max(0, token.length), line.to);
        if (to <= from) continue;
        resolved.push({ from, to, type: token.type });
    }
    resolved.sort((a, b) => a.from - b.from || a.to - b.to);

    const builder = new RangeSetBuilder<Decoration>();
    for (const t of resolved) {
        builder.add(
            t.from,
            t.to,
            Decoration.mark({ class: tokenClassMap[t.type] }),
        );
    }

    return builder.finish();
}

const semanticTheme = EditorView.baseTheme({
    ".cm-intelligence-token-directive, .cm-intelligence-token-for-keyword, .cm-intelligence-token-meta-keyword, .cm-intelligence-token-import-keyword, .cm-intelligence-token-use-keyword, .cm-intelligence-token-content-keyword":
    {
        color: "var(--syntax-keyword)",
        fontWeight: "600",
    },
    ".cm-intelligence-token-definition-id, .cm-intelligence-token-symbol-name": {
        color: "var(--syntax-variable)",
    },
    ".cm-intelligence-token-plugin-name": {
        color: "var(--syntax-string)",
    },
    ".cm-intelligence-token-ref-path": {
        color: "var(--syntax-function)",
    },
    ".cm-intelligence-token-config-key": {
        color: "var(--syntax-attribute)",
    },
    ".cm-intelligence-token-fence-lang": {
        color: "var(--syntax-comment)",
        fontStyle: "italic",
    },
    ".cm-intelligence-token-namespace": {
        color: "var(--syntax-type)",
    },
    ".cm-intelligence-token-file-path": {
        color: "var(--syntax-link)",
        textDecoration: "underline",
    },
});

const setTokens = StateEffect.define<IntelligenceSemanticToken[]>();

export function intelligenceSemanticTokens(
    config: IntelligenceConfig,
): Extension {
    const resolveSession = createIntelligenceSessionResolver(config);
    const getSyncVersion = () => config.getSyncVersion?.() ?? 0;

    const rawField = StateField.define<IntelligenceSemanticToken[]>({
        create: () => [],
        update(tokens, tr) {
            for (const effect of tr.effects) {
                if (effect.is(setTokens)) return effect.value;
            }
            return tokens;
        },
    });

    const field = StateField.define<DecorationSet>({
        create: () => Decoration.none,
        update(decos, tr) {
            for (const effect of tr.effects) {
                if (effect.is(setTokens)) {
                    return buildSemanticTokenDecorations(tr.state, effect.value);
                }
            }
            return tr.docChanged
                ? buildSemanticTokenDecorations(tr.state, tr.state.field(rawField))
                : decos.map(tr.changes);
        },
        provide: (f) => EditorView.decorations.from(f),
    });

    const refresher = ViewPlugin.define((view) => {
        let pending: ReturnType<typeof setTimeout> | null = null;
        let requestVersion = 0;
        let lastAppliedKey: string | null = null;

        const clearTokens = () => {
            lastAppliedKey = null;
            view.dispatch({ effects: setTokens.of([]) });
        };

        const refresh = () => {
            const engine = config.getEngine();
            if (!engine?.intelligenceSemanticTokens) {
                clearTokens();
                return;
            }

            const currentVersion = ++requestVersion;
            resolveSession()
                .then((session) => {
                    if (!session?.engine.intelligenceSemanticTokens) {
                        clearTokens();
                        return null;
                    }
                    const requestKey = `${session.sessionId}:${getSyncVersion()}`;
                    if (requestKey === lastAppliedKey) {
                        return null;
                    }
                    return session.engine.intelligenceSemanticTokens({
                        sessionId: session.sessionId,
                    }).then((result) => ({ requestKey, result }));
                })
                .then((result) => {
                    if (!result || currentVersion !== requestVersion) return;
                    lastAppliedKey = result.requestKey;
                    view.dispatch({ effects: setTokens.of(result.result.tokens ?? []) });
                })
                .catch(() => {
                    if (currentVersion !== requestVersion) return;
                    clearTokens();
                });
        };

        const schedule = (delay: number) => {
            if (pending) clearTimeout(pending);
            pending = setTimeout(() => {
                pending = null;
                refresh();
            }, delay);
        };

        const unsubscribe = config.subscribeSessionId?.(() => {
            requestVersion += 1;
            clearTokens();
            schedule(0);
        });
        const unsubscribeSyncVersion = config.subscribeSyncVersion?.(() => {
            requestVersion += 1;
            schedule(0);
        });

        if (config.getSessionId()) {
            schedule(0);
        }

        // No initial timer: subscribeSessionId fires when session becomes
        // available, which schedules the first refresh with delay 0.

        return {
            update(update: ViewUpdate) {
                void update;
            },
            destroy() {
                if (pending) clearTimeout(pending);
                unsubscribe?.();
                unsubscribeSyncVersion?.();
            },
        };
    });

    return [rawField, field, refresher, semanticTheme];
}
